<template>
  <mc-sidepanel :active="open" @close="dismiss">
    <template #title>
      <div class="title is-5 is-marginless">Select Image</div>
    </template>
    <template #actions>
      <div class="level-item">
        <b-field>
          <b-input
            v-model="searchQuery"
            icon="magnify"
            placeholder="Search by tags"
            type="search"
            @input="searchHandler"
          />
        </b-field>
      </div>
    </template>
    <template #default>
      <div v-if="open" class="images">
        <mc-paginated-list resource-state="images">
          <div class="images__container">
            <div v-masonry transition-duration="0.3s" item-selector=".item">
              <div class="images-list section">
                <div class="item image images-list__item">
                  <section v-if="images.uploading" class="section">
                    <div class="content has-text-centered">
                      <p>
                        <b-icon icon="upload" size="is-large" />
                      </p>
                      <progress
                        class="progress is-small"
                        :value="images.uploadProgress"
                        max="100"
                      />
                      <p>Uploading: {{ images.uploadProgress.toFixed(2) }} %</p>
                    </div>
                  </section>
                  <b-field v-else>
                    <b-upload
                      v-model="files"
                      drag-drop
                      v-bind="$attrs"
                      @input="fileUploadHandler"
                    >
                      <slot>
                        <section class="section">
                          <div class="content has-text-centered">
                            <p>
                              <b-icon icon="upload" size="is-large" />
                            </p>
                            <p>Drop your file here or click to upload</p>
                          </div>
                        </section>
                      </slot>
                    </b-upload>
                  </b-field>
                </div>
                <div
                  v-if="images.loadingPage || images.searching"
                  class="image item images-list__item"
                >
                  <section class="section">
                    <div class="content has-text-centered">
                      <b-loading :is-full-page="false" active />
                      <p>Fetching results!</p>
                    </div>
                  </section>
                </div>
                <div
                  v-else-if="imagesList.length === 0"
                  class="image item images-list__item"
                >
                  <section class="section">
                    <div class="content has-text-centered">
                      <p>
                        <b-icon icon="image-broken" size="is-large" />
                      </p>
                      <p>No Images available!</p>
                    </div>
                  </section>
                </div>
                <div
                  v-for="(image, index) in imagesList"
                  v-else
                  :key="index"
                  v-masonry-tile
                  class="image item images-list__item"
                  @click="imageSelectHandler(image)"
                >
                  <mc-url-parse :src="image.optimizedPath">
                    <template #temp="{ src: url }">
                      <img
                        v-if="!image.processing"
                        :src="url"
                        @load="redrawVueMasonry"
                      />
                      <section v-else class="section">
                        <div class="content has-text-centered">
                          <b-loading :is-full-page="false" active />
                          <p>Image is being processed!</p>
                        </div>
                      </section>
                    </template>
                  </mc-url-parse>
                </div>
              </div>
            </div>
          </div>
        </mc-paginated-list>
      </div>
    </template>
  </mc-sidepanel>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import debounce from 'lodash/debounce';

import MetaModal from './MetaModal';

export default {
  props: {
    value: { type: String, default: '' },
    open: { type: Boolean, required: true },
    type: {
      type: String,
      required: true,
      validator: value => ['POSTER', 'OTHER'].indexOf(value) !== -1
    },
    pathPrefix: { type: String, default: '' }
  },
  data() {
    return { files: [], metaModalActive: false, tags: [], searchQuery: '' };
  },
  computed: {
    ...mapState(['images']),
    imagesList() {
      return this.searchQuery ? this.images.searchResults : this.images.list;
    }
  },
  watch: {
    open: function(val) {
      if (val) {
        this.setFilter({
          type: this.type
        });
        this.searchQuery = '';
      }
    }
  },
  created() {
    const escapeHandler = e => {
      if (e.key == 'Escape' && this.open) this.dismiss();
    };
    document.addEventListener('keydown', escapeHandler);
    this.$once('hook:destroyed', () =>
      document.removeEventListener('keydown', escapeHandler)
    );
    this.$watch(() => {
      return `${this.images.list.reduce((processing, image) => {
        return processing || image.processing;
      }, false)}-${this.images.list.length}`;
    }, this.redrawVueMasonry);
  },
  methods: {
    ...mapActions('images', ['uploadImages', 'setFilter', 'search']),
    searchHandler(query) {
      this.search({
        query,
        filters: `type:${this.type}`
      });
    },
    dismiss() {
      this.$emit('close');
    },
    imageSelectHandler(image) {
      this.$emit('input', image.optimizedPath);
    },
    getImageMetaData() {
      return new Promise((resolve, reject) => {
        this.$buefy.modal.open({
          parent: this,
          component: MetaModal,
          hasModalCard: true,
          events: {
            input: resolve
          },
          onCancel: reject
        });
      });
    },
    async fileUploadHandler(files) {
      try {
        await Promise.all(
          (Array.isArray(files) ? files : [files]).map(async file => {
            return this.uploadImages({
              file: file,
              type: this.type,
              pathPrefix: this.pathPrefix,
              ...(await this.getImageMetaData())
            });
          })
        );
        this.$buefy.toast.open({
          message:
            'File Upload Done! Will appear in the list after server resizes it!',
          type: 'is-success'
        });
      } catch (e) {
        this.$buefy.toast.open({
          message: `Upload Failed! ${this.images.uploadError}`,
          type: 'is-danger',
          duration: 5000
        });
      } finally {
        this.reset();
      }
    },
    redrawVueMasonry() {
      this.$redrawVueMasonry();
    },
    reset() {
      this.files = [];
    }
  }
};
</script>

<style lang="scss" scoped>
.images {
  &__container {
    padding: 0 0.5rem;
    margin: auto;
  }
  &-list {
    &__item {
      width: 23.5%;
      margin: 0.3rem;
      padding: 0rem;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: rgba(30, 30, 30, 0.2);
      &:hover {
        opacity: 0.7;
        cursor: grab;
      }
      -webkit-box-shadow: 0px 0px 5px 1px rgba(122, 122, 122, 0.7);
      -moz-box-shadow: 0px 0px 5px 1px rgba(122, 122, 122, 0.7);
      box-shadow: 0px 0px 5px 1px rgba(122, 122, 122, 0.7);
    }
  }
}
</style>
